import { create } from 'zustand';
import type { UploadItem, UploadStatus } from '@/upload/uploadTypes';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type UploadQueueStatus = 'idle' | 'uploading' | 'paused' | 'complete';

interface UploadState {
  /** Files waiting to be processed. */
  queue: UploadItem[];
  /** Files currently being uploaded (keyed by client id). */
  activeUploads: Map<string, UploadItem>;
  /** Running count of successfully completed files. */
  completedCount: number;
  /** Files that have exhausted all retries. */
  failedItems: UploadItem[];
  /** Total number of files added for this session. */
  totalFiles: number;
  /** Total bytes across all files for this session. */
  totalBytes: number;
  /** Cumulative bytes confirmed uploaded so far. */
  uploadedBytes: number;
  /** Overall queue status. */
  status: UploadQueueStatus;
  /** Server-assigned session id. */
  sessionId: string | null;
  /** Event id this session belongs to. */
  eventId: string | null;

  // Actions
  enqueueFiles: (files: File[]) => void;
  startUpload: (sessionId: string, eventId: string) => void;
  pauseUpload: () => void;
  resumeUpload: () => void;
  retryFailed: () => void;
  removeItem: (id: string) => void;
  updateItemStatus: (id: string, status: UploadStatus, extra?: Partial<UploadItem>) => void;
  updateItemProgress: (id: string, progress: number, bytesUploaded?: number) => void;
  markItemCompleted: (id: string, fileBytes: number) => void;
  markItemFailed: (id: string, error: string) => void;
  activateItem: (id: string) => void;
  deactivateItem: (id: string) => void;
  dequeueNext: () => UploadItem | null;
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

let counter = 0;

function generateClientId(): string {
  return `ul_${Date.now()}_${counter++}`;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

const initialState = {
  queue: [] as UploadItem[],
  activeUploads: new Map<string, UploadItem>(),
  completedCount: 0,
  failedItems: [] as UploadItem[],
  totalFiles: 0,
  totalBytes: 0,
  uploadedBytes: 0,
  status: 'idle' as UploadQueueStatus,
  sessionId: null as string | null,
  eventId: null as string | null,
};

export const useUploadStore = create<UploadState>()((set, get) => ({
  ...initialState,

  // -- Enqueue files (before upload starts) --------------------------------
  enqueueFiles: (files: File[]) => {
    const items: UploadItem[] = files.map((file) => ({
      id: generateClientId(),
      file,
      status: 'pending' as const,
      progress: 0,
      retryCount: 0,
    }));

    const addedBytes = files.reduce((sum, f) => sum + f.size, 0);

    set((s) => ({
      queue: [...s.queue, ...items],
      totalFiles: s.totalFiles + files.length,
      totalBytes: s.totalBytes + addedBytes,
    }));
  },

  // -- Start -----------------------------------------------------------------
  startUpload: (sessionId, eventId) => {
    set({ sessionId, eventId, status: 'uploading' });
  },

  // -- Pause / Resume -------------------------------------------------------
  pauseUpload: () => set({ status: 'paused' }),
  resumeUpload: () => set({ status: 'uploading' }),

  // -- Retry all failed items -----------------------------------------------
  retryFailed: () => {
    const { failedItems } = get();
    const retried: UploadItem[] = failedItems.map((item) => ({
      ...item,
      status: 'pending' as const,
      progress: 0,
      error: undefined,
      // retryCount is preserved so the manager can enforce limits
    }));

    set((s) => ({
      queue: [...s.queue, ...retried],
      failedItems: [],
    }));
  },

  // -- Remove a single item -------------------------------------------------
  removeItem: (id) =>
    set((s) => {
      const item = s.queue.find((i) => i.id === id);
      const newActive = new Map(s.activeUploads);
      newActive.delete(id);

      return {
        queue: s.queue.filter((i) => i.id !== id),
        activeUploads: newActive,
        failedItems: s.failedItems.filter((i) => i.id !== id),
        totalFiles: item ? s.totalFiles - 1 : s.totalFiles,
        totalBytes: item ? s.totalBytes - item.file.size : s.totalBytes,
      };
    }),

  // -- Status updates -------------------------------------------------------
  updateItemStatus: (id, status, extra) =>
    set((s) => ({
      queue: s.queue.map((item) =>
        item.id === id ? { ...item, status, ...extra } : item,
      ),
    })),

  updateItemProgress: (id, progress, bytesUploaded) =>
    set((s) => {
      // Find old item to compute byte delta.
      const oldItem = s.activeUploads.get(id);
      const oldProgress = oldItem?.progress ?? 0;
      const fileSize = oldItem?.file.size ?? 0;

      const oldBytes = Math.round((oldProgress / 100) * fileSize);
      const newBytes = bytesUploaded ?? Math.round((progress / 100) * fileSize);
      const bytesDelta = newBytes - oldBytes;

      const updated: UploadItem = oldItem
        ? { ...oldItem, progress, status: 'uploading' }
        : { id, file: new File([], ''), progress, status: 'uploading', retryCount: 0 };

      const newActive = new Map(s.activeUploads);
      newActive.set(id, updated);

      return {
        queue: s.queue.map((item) =>
          item.id === id ? { ...item, progress, status: 'uploading' } : item,
        ),
        activeUploads: newActive,
        uploadedBytes: s.uploadedBytes + Math.max(0, bytesDelta),
      };
    }),

  markItemCompleted: (id, fileBytes) =>
    set((s) => {
      const newActive = new Map(s.activeUploads);
      newActive.delete(id);

      // Correct uploadedBytes: remove any partial progress and add the full file.
      const oldItem = s.activeUploads.get(id);
      const prevBytes = oldItem
        ? Math.round((oldItem.progress / 100) * oldItem.file.size)
        : 0;
      const correctedDelta = fileBytes - prevBytes;

      const newCompleted = s.completedCount + 1;
      const allDone =
        newCompleted === s.totalFiles && s.failedItems.length === 0;

      return {
        queue: s.queue.map((item) =>
          item.id === id
            ? { ...item, status: 'completed' as const, progress: 100, error: undefined }
            : item,
        ),
        activeUploads: newActive,
        completedCount: newCompleted,
        uploadedBytes: s.uploadedBytes + Math.max(0, correctedDelta),
        status: allDone ? 'complete' : s.status,
      };
    }),

  markItemFailed: (id, error) =>
    set((s) => {
      const failedItem = s.queue.find((i) => i.id === id);
      const newActive = new Map(s.activeUploads);
      newActive.delete(id);

      return {
        queue: s.queue.map((item) =>
          item.id === id
            ? { ...item, status: 'failed' as const, error }
            : item,
        ),
        activeUploads: newActive,
        failedItems: failedItem
          ? [...s.failedItems, { ...failedItem, status: 'failed' as const, error }]
          : s.failedItems,
      };
    }),

  // -- Active pool management ------------------------------------------------
  activateItem: (id) =>
    set((s) => {
      const item = s.queue.find((i) => i.id === id);
      if (!item) return s;
      const newActive = new Map(s.activeUploads);
      newActive.set(id, item);
      return { activeUploads: newActive };
    }),

  deactivateItem: (id) =>
    set((s) => {
      const newActive = new Map(s.activeUploads);
      newActive.delete(id);
      return { activeUploads: newActive };
    }),

  /** Pull the next pending item from the front of the queue. Returns null if empty. */
  dequeueNext: () => {
    const { queue } = get();
    const next = queue.find((i) => i.status === 'pending');
    return next ?? null;
  },

  // -- Full reset -----------------------------------------------------------
  reset: () => set({ ...initialState, activeUploads: new Map() }),
}));
